import { Container, ContainerChild, Graphics, Text } from "pixi.js";

import type {
  IStudioWidget,
  IStudioWidgetViewers,
  ViewerPlatform,
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";
import ApiIntegrationsRepository from "../../../../infrastructure/Repositories/Integrations/ApiIntegrationsRepository";
import { getViewersDesign } from "../../../../data/viewersDesignsCatalog";
import { ViewersDesignRenderer } from "./ViewersDesignRenderer";

interface WidgetState {
  destination: { x: number; y: number; width: number; height: number };
  options: IStudioWidgetViewers["options"];
  /**
   * Set ONLY when `options.designId` resolves to a catalog design — the legacy
   * Text/Graphics fields below are then absent and the design sprite is driven
   * via `designRenderer.update(parts)` from `refresh`.
   */
  designRenderer?: ViewersDesignRenderer;
  bg?: Graphics;
  dot?: Graphics;
  countText?: Text;
  labelText?: Text;
  breakdownText?: Text;
}

const POLL_INTERVAL_MS = 30_000;

const repo = new ApiIntegrationsRepository();

const PLATFORM_LABEL: Record<ViewerPlatform, string> = {
  twitch: "Twitch",
  youtube: "YouTube",
  kick: "Kick",
  restream: "Restream",
  livepeer: "Livepeer",
  facebook: "Facebook",
};

const formatCompact = (n: number): string => {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
};

const formatNumber = (n: number, compact: boolean): string =>
  compact ? formatCompact(n) : n.toLocaleString();

const parseHex = (input: string | undefined, fallback: number): number => {
  if (!input) return fallback;
  const m = input.match(/^#?([0-9a-f]{6})$/i);
  return m ? parseInt(m[1], 16) : fallback;
};

const parseAlpha = (input: string | undefined): number => {
  if (!input) return 1;
  const rgba = input.match(
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*([\d.]+))?\s*\)$/i,
  );
  if (rgba && rgba[1] !== undefined) return parseFloat(rgba[1]);
  return 1;
};

const fillFromColor = (input: string | undefined, fallback: number) => ({
  color: parseHex(input, fallback),
  alpha: parseAlpha(input),
});

/**
 * Pixi renderer for the live Viewers widget. Polls
 * `/api/integrations/viewers` every 30s and sums the viewer counts of
 * every platform listed in `options.selectedPlatforms`.
 */
export class ViewersWidgetRenderer {
  private widgets = new Map<string, WidgetState>();
  private timers = new Map<string, ReturnType<typeof setInterval>>();

  /** Stop polling + free a widget id's prior render state (design or legacy). */
  private teardownWidget(id: string): void {
    const t = this.timers.get(id);
    if (t) {
      clearInterval(t);
      this.timers.delete(id);
    }
    const prev = this.widgets.get(id);
    if (prev?.designRenderer) prev.designRenderer.destroy();
    this.widgets.delete(id);
  }

  render(
    widget: IStudioWidget,
    container: Container,
    destination: { x: number; y: number; width: number; height: number },
  ): void {
    const w = widget as IStudioWidgetViewers;
    const o = w.options;

    // Tear down any prior render state for this id (the factory hands us a fresh
    // container, but the poll timer + design texture must be freed here).
    this.teardownWidget(widget.id);

    // ── Design-store path ──────────────────────────────────────────────────
    // When `options.designId` resolves to a catalog design, render that design's
    // card SVG (driven by the same 30s poll) and skip the legacy text overlay.
    const design = getViewersDesign(o.designId);
    if (design) {
      const designRenderer = new ViewersDesignRenderer(design);
      designRenderer.render(container, destination);
      this.widgets.set(widget.id, { destination, options: o, designRenderer });
      void this.refresh(widget.id);
      const t = setInterval(() => {
        void this.refresh(widget.id);
      }, POLL_INTERVAL_MS);
      this.timers.set(widget.id, t);
      return;
    }

    let bg: Graphics | undefined;
    if (o.backgroundColor && o.backgroundColor !== "transparent") {
      bg = new Graphics();
      const fill = fillFromColor(o.backgroundColor, 0x0a0014);
      bg.roundRect(0, 0, destination.width, destination.height, 12).fill({
        color: fill.color,
        alpha: fill.alpha,
      });
      container.addChild(bg);
    }

    const fontFamily = o.fontFamily ?? "Inter, system-ui, sans-serif";
    const fontWeight = o.bold ? "bold" : "500";
    const fontStyle = o.italic ? "italic" : "normal";
    const baseFontSize = o.fontSize ? parseInt(o.fontSize) : 28;
    const labelFontSize = baseFontSize * 0.45;
    const breakdownFontSize = baseFontSize * 0.4;
    const textColor = o.textColor ?? "#ffffff";
    const align = o.textAlign ?? "center";

    const padX = destination.width * 0.05;
    const padY = destination.height * 0.16;

    const anchorX = align === "left" ? 0 : align === "right" ? 1 : 0.5;
    const baseX =
      align === "left"
        ? padX
        : align === "right"
          ? destination.width - padX
          : destination.width / 2;

    let cursorY = padY;

    let labelText: Text | undefined;
    if (o.showLabel) {
      const fallback =
        o.selectedPlatforms.length === 1
          ? PLATFORM_LABEL[o.selectedPlatforms[0]]
          : "Viewers";
      const text = (o.label || fallback).toUpperCase();
      labelText = new Text({
        text,
        style: {
          fontFamily,
          fontSize: labelFontSize,
          fill: textColor,
          fontWeight,
          fontStyle,
          letterSpacing: 1.6,
          align,
        },
      });
      labelText.anchor.set(anchorX, 0);
      labelText.x = baseX;
      labelText.y = cursorY;
      labelText.alpha = 0.85;
      container.addChild(labelText as unknown as ContainerChild);
      cursorY += labelFontSize * 1.2;
    }

    const countText = new Text({
      text: "0",
      style: {
        fontFamily,
        fontSize: baseFontSize,
        fill: textColor,
        fontWeight,
        fontStyle,
        align,
      },
    });
    countText.anchor.set(anchorX, 0);
    countText.x = baseX;
    countText.y = cursorY;
    container.addChild(countText as unknown as ContainerChild);

    let dot: Graphics | undefined;
    if (o.showIcon) {
      const dotR = baseFontSize * 0.18;
      const accent = fillFromColor(o.accentColor, 0xff4d4d);
      dot = new Graphics();
      dot.circle(0, 0, dotR).fill({ color: accent.color, alpha: accent.alpha });
      dot.y = cursorY + baseFontSize * 0.55;
      container.addChild(dot);
    }
    cursorY += baseFontSize * 1.05;

    let breakdownText: Text | undefined;
    if (o.selectedPlatforms.length >= 2 && o.showPlatformBreakdown) {
      breakdownText = new Text({
        text: "",
        style: {
          fontFamily,
          fontSize: breakdownFontSize,
          fill: textColor,
          fontWeight: "normal",
          fontStyle,
          align,
        },
      });
      breakdownText.anchor.set(anchorX, 0);
      breakdownText.x = baseX;
      breakdownText.y = cursorY;
      breakdownText.alpha = 0.75;
      container.addChild(breakdownText as unknown as ContainerChild);
    }

    const state: WidgetState = {
      destination,
      options: o,
      bg,
      dot,
      countText,
      labelText,
      breakdownText,
    };
    this.widgets.set(widget.id, state);

    void this.refresh(widget.id);
    const existing = this.timers.get(widget.id);
    if (existing) clearInterval(existing);
    const t = setInterval(() => {
      void this.refresh(widget.id);
    }, POLL_INTERVAL_MS);
    this.timers.set(widget.id, t);
  }

  private async refresh(widgetId: string): Promise<void> {
    const state = this.widgets.get(widgetId);
    if (!state) return;
    const { options: o } = state;

    let count = 0;
    let breakdown = "";
    const per = { twitch: 0, youtube: 0, kick: 0, restream: 0, facebook: 0 };

    try {
      const res = await repo.getViewers();
      const data = res?.data ?? [];

      const selected = new Set<ViewerPlatform>(o.selectedPlatforms);
      const perPlatform = data.filter((d) =>
        selected.has(d.platform.toLowerCase() as ViewerPlatform),
      );
      count = perPlatform.reduce((acc, d) => acc + (d.viewers ?? 0), 0);

      // Per-platform totals (selected only) for the design cards that break the
      // count down by network.
      for (const d of perPlatform) {
        const slug = d.platform.toLowerCase();
        if (slug in per) {
          per[slug as keyof typeof per] += d.viewers ?? 0;
        }
      }

      if (o.selectedPlatforms.length >= 2 && o.showPlatformBreakdown) {
        breakdown = o.selectedPlatforms
          .map((slug) => {
            const entry = data.find((d) => d.platform.toLowerCase() === slug);
            const n = entry?.viewers ?? 0;
            return `${PLATFORM_LABEL[slug]} ${formatNumber(n, o.compactNumber)}`;
          })
          .join("  ·  ");
      }
    } catch (err) {
      console.warn("[ViewersWidget] viewers fetch failed", err);
    }

    // Design-store path: hand the resolved counts to the sprite renderer.
    if (state.designRenderer) {
      state.designRenderer.update({ total: count, ...per });
      return;
    }

    const { countText, breakdownText, dot } = state;
    if (!countText) return;

    countText.text = formatNumber(count, o.compactNumber);
    if (breakdownText) breakdownText.text = breakdown;

    if (dot) {
      const align = o.textAlign ?? "center";
      const anchorX = align === "left" ? 0 : align === "right" ? 1 : 0.5;
      const dotR = parseInt(o.fontSize ?? "28") * 0.18;
      const gap = dotR * 1.2;
      if (anchorX === 0) {
        dot.x = countText.x - gap;
        countText.x += dotR * 2 + gap;
      } else if (anchorX === 1) {
        dot.x = countText.x - countText.width - gap;
      } else {
        dot.x = countText.x - countText.width / 2 - gap;
      }
    }
  }

  destroy(): void {
    for (const t of this.timers.values()) clearInterval(t);
    this.timers.clear();
    for (const state of this.widgets.values()) {
      state.designRenderer?.destroy();
    }
    this.widgets.clear();
  }
}
